import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
} from "react";

import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { Draggable } from "./Draggable";

type DroppableProps = {
  children: React.ReactNode;
  id: string;
  itemIds: string[];
  direction?: "horizontal" | "vertical";
  emptyAreaElement?: React.ReactNode;
  emptyAreaStyle?: React.CSSProperties;
};

const PlaceholderContext = createContext<{
  didRender: React.MutableRefObject<boolean>;
  placeholder: ReactNode;
} | null>(null);

type DroppablePlaceholderProps = { _doNotSet?: boolean };

export const DroppablePlaceholder = ({
  _doNotSet: auto,
}: DroppablePlaceholderProps) => {
  const placeholderContext = useContext(PlaceholderContext);

  if (!auto) {
    placeholderContext.didRender.current = true;
  }
  if (!placeholderContext) {
    throw Error("PlaceholderContext is not provided.");
  }

  if (auto && placeholderContext.didRender.current) {
    return null;
  }

  return placeholderContext.placeholder;
};

export const Droppable = ({
  children,
  itemIds,
  id,
  direction = "vertical",
  emptyAreaElement,
  emptyAreaStyle,
}: DroppableProps) => {
  const stableIds = (itemIds ?? []).join("___");
  const memoizedIds = useMemo(
    () => [...(stableIds ? stableIds.split("___") : []), `${id}-empty`],
    [stableIds, id],
  );

  const placeholderDidRender = useRef(false);
  placeholderDidRender.current = false;

  const placeholderProps = useMemo(
    () => ({
      didRender: placeholderDidRender,
      placeholder:
        !stableIds || stableIds.length === 0 ?
          <Draggable id={`${id}-empty`} placeholder>
            {emptyAreaElement ?
              emptyAreaElement
            : <div
                style={{ height: 10, width: "100%", ...(emptyAreaStyle ?? {}) }}
              />
            }
          </Draggable>
        : null,
    }),
    [emptyAreaElement, emptyAreaStyle, id, stableIds],
  );

  return (
    <SortableContext
      id={id}
      strategy={
        direction === "vertical" ?
          verticalListSortingStrategy
        : horizontalListSortingStrategy
      }
      items={memoizedIds}
    >
      <PlaceholderContext.Provider value={placeholderProps}>
        {children}
        <DroppablePlaceholder _doNotSet />
      </PlaceholderContext.Provider>
    </SortableContext>
  );
};
